import React, { useEffect, useRef } from "react";
import { getThemedStyles, styles } from "./CommentWithReplies.styles";
import { ICommentWithReplies } from "./CommentWithReplies.types";
import Comment from "../Comment/Comment";
import Replies from "../Replies/Replies";
import LazyLoadedCommentEditor from "../CommentEditor/LazyLoadedCommentEditor";
import { useToggleState } from "../../../../../utils/common/dataTypes/boolean";
import { useCommentsList } from "../../../../../utils/BrandedCommunity/Post/components/CommentSection/useCommentsList";
import Button from "../../../../common/Button/Button";
import { classNames } from "../../../../../utils/common/styling";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import { COMMENTS_ORDER } from "../../../../../constants/BrandedCommunity/Comments";
import useInsertTextAtCursor from "../../../../../features/Common/modules/Form/utils/useInsertTextAtCursor";
import { useCreatorThemeContext } from "../../../../../features/Common/modules/Theme/CreatorThemeContext";
import { pluralise } from "../../../../../features/Common/modules/DataTypes/utils/strings";

const CommentWithReplies = ({
  data,
  userImgSrc,
  userUuid,
  isCreator,
  creatorUuid,
  loggedInUserFullName,
  userAvatarPreference,
  cdnBaseUrl,
  fetchCdnAsset,
  enableHideUnhideComment,
  loadHighlightedReply,
  showHighlightedReply,
  highlightedReply,
  setHighlightedReply,
  onCommentLikeClick,
  callGetCommentLikedByMembersListApi,
  onCommentUpdate,
  onCommentDelete,
  callSubmitReplyApi,
  callGetCommentRepliesApi,
  callLikeReplyApi,
  callEditReplyApi,
  callDeleteReplyApi,
  notifySuccess,
  notifyError,
  onMemberClick,
  CommentEditor,
  canReport,
  onReport,
  showReported,
  highlightedReplyExpanded,
  CommentWrapper,
}: ICommentWithReplies.ICommentWithRepliesProps) => {
  const theme = useCreatorThemeContext();
  const themedStyles = getThemedStyles({ theme });

  const replyInputRef = useRef<HTMLInputElement>(null);
  const { insertTextAtCursor } = useInsertTextAtCursor({
    inputRef: replyInputRef,
  });

  const {
    onSubmit: onSubmitReply,
    data: replies,
    totalComments: totalReplies,
    showLoadMoreCommentsBtn: showLoadMoreRepliesBtn,
    loadComments: loadReplies,
    onLikeClick: onReplyLikeClick,
    onUpdate: onReplyUpdate,
    onDelete: onReplyDelete,
  } = useCommentsList({
    commentsOrder: COMMENTS_ORDER.AESCENDING,
    userUuid,
    isCreator,
    highlightedReply,
    setHighlightedReply,
    callSubmitApi: (args) =>
      callSubmitReplyApi({
        ...args,
        source_comment_uuid: data.uuid,
      }),
    callGetCommentsApi: (args) =>
      callGetCommentRepliesApi({
        ...args,
        source_comment_uuid: data.uuid,
      }),
    callLikeApi: callLikeReplyApi,
    callEditApi: callEditReplyApi,
    callDeleteApi: callDeleteReplyApi,
    notifySuccess,
    notifyError,
  });

  const [repliesAreShown, showReplies] = useToggleState();

  const onShowReplies = () => {
    loadReplies();
    showReplies();

    /** Loading highlighted reply in case replies are clicked otherwise this step can be skipped */
    if (showHighlightedReply && loadHighlightedReply) loadHighlightedReply();
  };

  useEffect(() => {
    const handleExpandReplies = () => {
      if (highlightedReplyExpanded) onShowReplies();
    };

    handleExpandReplies();
  }, []);

  const [replyInputIsShown, showReplyInput] = useToggleState();

  const focusReplyInput = () => {
    if (!replyInputIsShown) showReplyInput();
    if (replyInputRef.current) {
      replyInputRef.current.focus();
      replyInputRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  const onReplyCtaClick = focusReplyInput;

  /**
   * `data.replies_count` is the initial value
   * `totalReplies` is the latest value, if it is not `undefined`
   */
  const repliesCount = totalReplies ?? data.replies_count;

  const CommentEditorComponent = CommentEditor
    ? CommentEditor
    : LazyLoadedCommentEditor;

  return (
    <Comment
      data={data}
      userUuid={userUuid}
      isCreator={isCreator}
      creatorUuid={creatorUuid}
      userImgSrc={userImgSrc}
      loggedInUserFullName={loggedInUserFullName}
      enableHideUnhideComment={enableHideUnhideComment}
      fetchCdnAsset={fetchCdnAsset}
      cdnBaseUrl={cdnBaseUrl}
      userAvatarPreference={userAvatarPreference}
      CommentEditor={CommentEditor}
      onLikeClick={onCommentLikeClick}
      callGetLikedByMembersListApi={callGetCommentLikedByMembersListApi}
      onUpdate={onCommentUpdate}
      onDelete={onCommentDelete}
      onReplyCtaClick={onReplyCtaClick}
      repliesChild={
        <>
          {!repliesAreShown && repliesCount ? (
            <Button
              className={classNames(
                styles.showRepliesButton,
                themedStyles.showRepliesButton,
              )}
              onClick={onShowReplies}
              color="unset"
              size="unset"
            >
              <ArrowDropDownRoundedIcon /> {repliesCount}{" "}
              {pluralise("Reply", repliesCount)}
            </Button>
          ) : null}
          <Replies
            show={repliesAreShown}
            data={replies}
            userUuid={userUuid}
            userImgSrc={userImgSrc}
            isCreator={isCreator}
            creatorUuid={creatorUuid}
            cdnBaseUrl={cdnBaseUrl}
            enableHideUnhideComment={enableHideUnhideComment}
            fetchCdnAsset={fetchCdnAsset}
            loggedInUserFullName={loggedInUserFullName}
            userAvatarPreference={userAvatarPreference}
            showHighlightedReply={showHighlightedReply}
            highlightedReply={highlightedReply}
            CommentEditor={CommentEditor}
            onReplyLikeClick={onReplyLikeClick}
            callGetReplyLikedByMembersListApi={
              callGetCommentLikedByMembersListApi
            }
            onReplyUpdate={onReplyUpdate}
            onReplyDelete={onReplyDelete}
            loadReplies={loadReplies}
            showLoadMoreRepliesBtn={showLoadMoreRepliesBtn}
            onReplyCtaClick={onReplyCtaClick}
            onMemberClick={onMemberClick}
            canReport={canReport}
            onReport={onReport}
            showReported={showReported}
          />
          <CommentEditorComponent
            inputRef={replyInputRef}
            show={replyInputIsShown}
            userAvatarPreference={userAvatarPreference}
            creatorTheme={useCreatorThemeContext()}
            commenterDetails={{
              profilePicUrl: userImgSrc,
              name: loggedInUserFullName || "",
              username: userUuid || "",
            }}
            cdnBaseUrl={cdnBaseUrl}
            insertTextAtCursor={insertTextAtCursor}
            classes={{ container: styles.replyTextAreaContainer }}
            onSubmit={onSubmitReply}
          />
        </>
      }
      onMemberClick={onMemberClick}
      canReport={canReport}
      onReport={onReport}
      showReported={showReported}
      CommentWrapper={CommentWrapper}
    />
  );
};

export default CommentWithReplies;
